/**
 * InviteCard — an inbound invite with accept/decline and a live countdown to
 * expiry (Phase D, AC-21). `game_join` routes into a room on accept;
 * `lets_match` hands off to the existing match flow.
 */

import { useState } from 'react';
import { useActivity } from '../../context/ActivityContext';
import { useExpiryCountdown } from '../../hooks/useExpiryCountdown';
import type { ConnectionInvite } from '../../types/social';
import './InviteCard.css';

interface Props {
  invite: ConnectionInvite;
  /** Route the recipient into the room after accepting a game_join. */
  onEnterRoom?: (roomId: string) => void;
  /** Route to the resulting match after accepting a lets_match. */
  onMatch?: (matchId: string) => void;
}

export function InviteCard({ invite, onEnterRoom, onMatch }: Props) {
  const { acceptInvite, declineInvite, removeInvite } = useActivity();
  const [busy, setBusy] = useState(false);
  // Invites are non-actionable the instant they expire (AC-21.2) — unlike
  // disappearing messages, they get no 60s grace. Pass graceMs:0 so the card
  // is removed at expiry, and gate Accept + the countdown label on `expired`
  // so an expired invite never shows "Expires in 0s" or a live Accept button.
  const countdown = useExpiryCountdown(invite.expiresAt, () => removeInvite(invite.id), 0);
  const expired = new Date(invite.expiresAt).getTime() <= Date.now();

  const title = invite.type === 'game_join' ? 'wants you to join their game' : "wants to match";

  const accept = async () => {
    setBusy(true);
    try {
      const result = await acceptInvite(invite.id);
      if (result?.roomId) onEnterRoom?.(result.roomId);
      else if (result?.matchId) onMatch?.(result.matchId);
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await declineInvite(invite.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`invite-card invite-card--${invite.type}`}>
      <div className="invite-card__body">
        <span className="invite-card__who">Profile {invite.fromProfileId.slice(0, 8)}</span>
        <span className="invite-card__title">{title}</span>
        {expired ? (
          <span className="invite-card__ttl invite-card__ttl--expired">Expired</span>
        ) : (
          countdown && <span className="invite-card__ttl">Expires in {countdown}</span>
        )}
      </div>
      <div className="invite-card__actions">
        <button
          className="invite-card__btn invite-card__btn--primary"
          disabled={busy || expired}
          onClick={accept}
        >
          {invite.type === 'game_join' ? 'Join' : 'Match'}
        </button>
        <button className="invite-card__btn" disabled={busy} onClick={decline}>
          Decline
        </button>
      </div>
    </div>
  );
}

export default InviteCard;
